import { Link } from "react-router-dom";
import { useReveal } from "@/lib/useReveal";

const EMAIL = "Info.cleanwaterdrop@gmail.com";

const SECTIONS = [
  {
    title: "About these terms",
    body: (
      <p>
        These Terms of Use ("Terms") apply when you visit or interact with the
        CLEAN Water Drop Association website. By using this website, you agree
        to these Terms. If you do not agree, please do not use the website.
      </p>
    ),
  },
  {
    title: "Website usage",
    body: (
      <>
        <p>You agree to use this website respectfully and lawfully. Please do not:</p>
        <ul className="mt-4 space-y-2 list-disc pl-5">
          <li>Submit false, misleading, or abusive information through our forms.</li>
          <li>Attempt to access, scan, or disrupt parts of the website you are not authorised to use.</li>
          <li>Use the website in any way that would harm, harass, or impersonate any other person.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Accuracy of information",
    body: (
      <p>
        We do our best to keep the information on this website accurate and up to
        date. As a growing community initiative, however, details may change from
        time to time. The website is provided on an "as is" basis without
        warranty of any kind. We recommend contacting us directly to confirm any
        specific details before relying on them.
      </p>
    ),
  },
  {
    title: "Donations and enquiries",
    body: (
      <p>
        Donation forms on this website capture your <em>intention to give</em>.
        A team member will follow up to complete your contribution personally.
        No payment is processed on this website. Submitting an enquiry does not
        create a legally binding commitment to donate.
      </p>
    ),
  },
  {
    title: "Limitation of liability",
    body: (
      <p>
        To the fullest extent permitted by law, CLEAN Water Drop Association is
        not liable for any direct, indirect, incidental, or consequential
        damages arising from your use of this website. The website is offered as
        a community resource and is not a substitute for professional advice,
        emergency services, or medical care. If you are in immediate danger,
        please contact your local emergency services.
      </p>
    ),
  },
  {
    title: "Intellectual property",
    body: (
      <p>
        All content on this website — including text, logos, imagery, and
        layout — is the property of CLEAN Water Drop Association or its
        contributors and is provided for personal, non-commercial use only.
        You may not reproduce, redistribute, or use our materials for
        commercial purposes without our written permission.
      </p>
    ),
  },
  {
    title: "External links",
    body: (
      <p>
        Our website may occasionally link to third-party resources. We are not
        responsible for the content, accuracy, or privacy practices of any
        external website.
      </p>
    ),
  },
  {
    title: "Changes to these terms",
    body: (
      <p>
        We may update these Terms of Use from time to time. Any updates will be
        published on this page. Continuing to use the website after changes are
        published means you accept the updated Terms.
      </p>
    ),
  },
  {
    title: "Contact",
    body: (
      <p>
        Questions about these Terms can be sent to{" "}
        <a href={`mailto:${EMAIL}`} className="text-[#2E8B8B] underline">{EMAIL}</a>.
      </p>
    ),
  },
];

export default function Terms() {
  useReveal();
  return (
    <div data-testid="terms-page">
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-16">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Terms of Use</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[72px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Using our website.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                Simple, practical terms that explain how this website is offered
                and used.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <aside className="lg:col-span-4 reveal lg:sticky lg:top-28">
            <p className="eyebrow">On this page</p>
            <ul className="mt-5 space-y-3">
              {SECTIONS.map((s, i) => (
                <li key={s.title}>
                  <a
                    href={`#section-${i}`}
                    className="text-sm text-slate-600 hover:text-[#0A2540] transition-colors"
                  >
                    {String(i + 1).padStart(2, "0")} — {s.title}
                  </a>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-outline mt-8 inline-flex" data-testid="terms-contact-cta">
              Have a question?
            </Link>
          </aside>

          <div className="lg:col-span-8 space-y-14 reveal">
            {SECTIONS.map((s, i) => (
              <section key={s.title} id={`section-${i}`} data-testid={`terms-section-${i}`}>
                <p className="font-heading text-sm text-slate-400 tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-heading mt-3 text-2xl sm:text-3xl text-[#0A2540] leading-snug">
                  {s.title}
                </h2>
                <div className="mt-5 text-[15px] text-slate-600 leading-[1.75] space-y-3">
                  {s.body}
                </div>
              </section>
            ))}
            <p className="pt-6 border-t border-slate-200 text-xs text-slate-400">
              Last updated: {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
